import { useEffect, useState } from 'react';
import { Star, ThumbsUp, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const Stars = ({ value, onChange, readOnly = false, size = 'md' }) => {
  const sizes = { sm: 'h-4 w-4', md: 'h-5 w-5', lg: 'h-6 w-6' };
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => !readOnly && onChange?.(n)}
          className={`${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
          data-testid={`star-${n}`}
        >
          <Star
            className={`${sizes[size]} ${
              value && n <= value
                ? 'text-rose-gold fill-rose-gold'
                : 'text-muted-foreground'
            }`}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
};

export default function ReviewsSection({ itemType, itemId }) {
  const { user } = useAuth();
  const [stats, setStats] = useState({ reviews: [], avg_rating: null, rating_count: 0, tried_count: 0 });
  const [myReview, setMyReview] = useState({ rating: 0, text: '', tried: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [statsRes] = await Promise.all([
        api.get('/reviews', { params: { item_type: itemType, item_id: itemId } }),
      ]);
      setStats(statsRes.data);
      if (user) {
        const myRes = await api.get('/reviews/me', { params: { item_type: itemType, item_id: itemId } });
        if (myRes.data && myRes.data.id) {
          setMyReview({
            rating: myRes.data.rating || 0,
            text: myRes.data.text || '',
            tried: !!myRes.data.tried,
          });
        }
      }
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemType, itemId, user?.id]);

  const submit = async () => {
    if (!user) {
      toast('Sign in to share your experience', {
        action: { label: 'Sign in', onClick: () => (window.location.href = '/login') },
      });
      return;
    }
    if (!myReview.tried && !myReview.rating && !myReview.text) {
      toast.error('Mark as tried, add a rating, or write a note');
      return;
    }
    setSaving(true);
    try {
      await api.post('/reviews', {
        item_type: itemType,
        item_id: itemId,
        rating: myReview.rating || null,
        text: myReview.text,
        tried: myReview.tried || (myReview.rating > 0),
      });
      toast.success('Thanks for sharing your feedback');
      load();
    } catch (e) {
      toast.error('Couldn’t save your review');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await api.delete('/reviews', { params: { item_type: itemType, item_id: itemId } });
      setMyReview({ rating: 0, text: '', tried: false });
      toast('Review removed');
      load();
    } finally {
      setSaving(false);
    }
  };

  const toggleTried = async () => {
    if (!user) {
      toast('Sign in to track what you’ve tried');
      return;
    }
    const next = !myReview.tried;
    setMyReview((m) => ({ ...m, tried: next }));
    try {
      await api.post('/reviews', {
        item_type: itemType,
        item_id: itemId,
        rating: myReview.rating || null,
        text: myReview.text,
        tried: next,
      });
      load();
    } catch (e) {
      setMyReview((m) => ({ ...m, tried: !next }));
      toast.error('Couldn’t update');
    }
  };

  return (
    <Card className="rounded-2xl bg-card border-border/70 p-6" data-testid="reviews-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl font-semibold text-ink">Real-women reviews</h2>
        <div className="text-right">
          {stats.avg_rating != null ? (
            <div className="flex items-center gap-2">
              <Stars value={Math.round(stats.avg_rating)} readOnly size="sm" />
              <span className="text-sm text-muted-foreground">
                {stats.avg_rating} · {stats.rating_count} review{stats.rating_count === 1 ? '' : 's'}
              </span>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">No reviews yet</span>
          )}
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-1">
            {stats.tried_count} tried this
          </p>
        </div>
      </div>

      {/* My review composer */}
      <div className="rounded-xl border border-border/70 bg-cream-paper p-4 mb-5">
        {!user ? (
          <p className="text-sm text-muted-foreground">
            <Link to="/login" className="underline underline-offset-4">Sign in</Link>{' '}
            to share your experience and help other women.
          </p>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Your rating</span>
                <Stars value={myReview.rating} onChange={(n) => setMyReview((m) => ({ ...m, rating: n }))} />
              </div>
              <Button
                type="button"
                size="sm"
                variant={myReview.tried ? 'default' : 'outline'}
                onClick={toggleTried}
                className={`rounded-full ${myReview.tried ? 'bg-primary text-primary-foreground' : ''}`}
                data-testid="tried-toggle"
              >
                <ThumbsUp className="h-3.5 w-3.5 mr-1" />
                {myReview.tried ? 'You tried this' : 'I tried this'}
              </Button>
            </div>
            <Textarea
              value={myReview.text}
              onChange={(e) => setMyReview((m) => ({ ...m, text: e.target.value }))}
              placeholder="How did it work for you? Texture, scent, results..."
              className="min-h-[80px] rounded-xl bg-card resize-none"
              data-testid="review-text-input"
            />
            <div className="mt-3 flex items-center gap-2">
              <Button
                size="sm"
                onClick={submit}
                disabled={saving}
                className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="submit-review-button"
              >
                {saving ? 'Saving...' : 'Save my feedback'}
              </Button>
              {(myReview.rating || myReview.text || myReview.tried) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={remove}
                  className="rounded-xl text-muted-foreground"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Other reviews */}
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading reviews...</p>
      ) : stats.reviews.length === 0 ? (
        <p className="text-sm text-muted-foreground">Be the first to leave a review.</p>
      ) : (
        <ul className="space-y-4">
          {stats.reviews.map((r) => (
            <li key={r.id} className="border-t border-border/60 pt-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">{r.user_name}</p>
                {r.rating && <Stars value={r.rating} readOnly size="sm" />}
              </div>
              {r.text && <p className="mt-1.5 text-sm text-foreground/85 leading-relaxed">{r.text}</p>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
